/**
 * Map Layer Styling
 *
 * This module centralizes all the visual styling logic for different types of map features.
 * It provides consistent styling across the application and makes it easy to modify
 * colors, weights, and fill patterns without touching component logic.
 *
 * Key responsibilities:
 * - Define styles for all feature types (field boundaries, buffers, plots, etc.)
 * - Handle color mapping for treatment areas
 * - Manage hatching patterns for buffer visualizations
 * - Provide consistent styling interface for Leaflet layers
 *
 * Feature types handled:
 * - fieldBoundary: White outlines of field edges
 * - fieldBuffer: Gray hatched buffer zones around field edges
 * - replicateBoundary: White outlines of replicate areas
 * - treatmentArea: Colored treatment plot areas
 * - plotBuffer: Blue hatched plot buffer zones
 * - plotCenter: Colored center areas of plots
 * - trialBoundary: Dashed white trial boundary lines
 * - abline: Red AB line guidance
 *
 * Used by: TestAreasVisualizer.tsx
 */

export function getFeatureStyle(feature: any): any {
  const type = feature?.properties?.type;
  const plotColor = feature?.properties?.plotColor;

  switch (type) {
    case 'fieldBoundary':
      return { color: 'white', weight: 3, fillOpacity: 0 };

    case 'fieldBuffer':
      return {
        weight: 1,
        color: '#000',
        fillColor: 'url(#field-buffer-hatch)',
        fillOpacity: 0.7,
      };

    case 'replicateBoundary':
      return {
        color: 'white',
        weight: 2,
        fillOpacity: 0,
      };

    case 'treatmentArea':
      return {
        color: 'transparent', // No individual boundaries
        weight: 0,
        fillColor: plotColor || '#007f66',
        fillOpacity: 0.8,
      };

    case 'plotBuffer':
      return {
        weight: 1,
        color: '#2196f3',
        fillColor: 'url(#plot-buffer-hatch)',
        fillOpacity: 0.7,
      };

    case 'plotCenter':
      return {
        color: plotColor || '#007f66',
        weight: 1,
        fillColor: plotColor || '#007f66',
        fillOpacity: 0.8,
      };

    case 'trialBoundary':
      return {
        color: '#87CEEB',
        weight: 2,
        fillOpacity: 0,
        dashArray: '5,5',
      };

    case 'abline':
      return {
        color: '#dc3545',
        weight: 3,
        fillOpacity: 0,
      };

    default:
      return { color: 'gray', weight: 1 };
  }
}
